/**
 * Knowledge Hub Dataplane API Service
 *
 * Provides CRUD operations for Knowledge Hubs via the Otto dataplane API.
 * In local/mock mode, returns mock data.
 *
 * Dataplane path:
 *   {logicEndpoint}/automationProjects/{projectName}/knowledgeHubs/{hubName}
 */

import { getLogicEndpoint, apiVersions } from './endpoints';
import { isLocalMode } from './localRuntimeClient';
import type {
  KnowledgeHub,
  KnowledgeHubType,
  KnowledgeArtifact,
  KnowledgeArtifactStatus,
  CreateFileHubRequest,
  CreateFoundryIQHubRequest,
  CreateAISearchHubRequest,
} from '@/types/knowledgeHub';
import { normalizeKnowledgeHubStatus } from '@/types/knowledgeHub';

const API_VERSION = apiVersions.otto;
const REQUEST_TIMEOUT = 30000;
// Base64 PUT uploads can be larger/slower than typical API requests; allow more time.
const UPLOAD_TIMEOUT = 120000;

/**
 * Structured error thrown by Knowledge Hub API calls.
 * Preserves the HTTP status code and the API error code so callers can
 * distinguish 4xx (user-actionable) from 5xx (transient/internal).
 */
export class KnowledgeHubApiError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly errorCode?: string,
  ) {
    super(message);
    this.name = 'KnowledgeHubApiError';
  }
}

function isMockMode(): boolean {
  const isE2EMode = import.meta.env.VITE_E2E_MODE === 'true';
  const isStaticMockMode = import.meta.env.VITE_STATIC_MOCK_MODE === 'true';
  const hasExplicitLogicEndpoint = !!(import.meta.env.VITE_LOGIC_ENDPOINT ||
    (typeof window !== 'undefined' && (window as any).__OTTO__LOGIC_ENDPOINT)); // eslint-disable-line @typescript-eslint/no-explicit-any

  // In mock/local/E2E mode, only use real API if the logic endpoint was explicitly configured
  return (isE2EMode || isStaticMockMode || isLocalMode()) && !hasExplicitLogicEndpoint;
}

// =============================================================================
// MOCK DATA (used in local/E2E mode)
// =============================================================================

const mockHubs: KnowledgeHub[] = [
  {
    id: 'mock-project/knowledgeHubs/hr-policies',
    name: 'hr-policies',
    type: 'file',
    properties: {
      description: 'Company-wide HR policies and guidelines',
      projectName: 'mock-project',
      status: 'ready',
      lastModified: '2026-03-24T14:15:00Z',
      sourceType: 'file',
      artifactCount: 3,
    },
    artifacts: [
      { id: 'mock-project/knowledgeHubs/hr-policies/knowledgeArtifacts/pto-policy.pdf', name: 'pto-policy.pdf', properties: { contentType: 'application/pdf', uploadStatus: 'Completed', createdAt: '2026-03-24T14:00:00Z' } },
      { id: 'mock-project/knowledgeHubs/hr-policies/knowledgeArtifacts/remote-work-guidelines.docx', name: 'remote-work-guidelines.docx', properties: { contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', uploadStatus: 'Completed', createdAt: '2026-03-24T14:05:00Z' } },
      { id: 'mock-project/knowledgeHubs/hr-policies/knowledgeArtifacts/benefits-summary.md', name: 'benefits-summary.md', properties: { contentType: 'text/markdown', uploadStatus: 'Completed', createdAt: '2026-03-24T14:10:00Z' } },
    ],
  },
  {
    id: 'mock-project/knowledgeHubs/employee-handbook',
    name: 'employee-handbook',
    type: 'file',
    properties: {
      description: 'Full employee handbook for fiscal year 2026',
      projectName: 'mock-project',
      status: 'ready',
      lastModified: '2026-03-22T10:30:00Z',
      sourceType: 'file',
      artifactCount: 1,
    },
    artifacts: [
      { id: 'mock-project/knowledgeHubs/employee-handbook/knowledgeArtifacts/employee-handbook-2026.pdf', name: 'employee-handbook-2026.pdf', properties: { contentType: 'application/pdf', uploadStatus: 'Completed', createdAt: '2026-03-22T10:30:00Z' } },
    ],
  },
  {
    id: 'mock-project/knowledgeHubs/onboarding-guide',
    name: 'onboarding-guide',
    type: 'file',
    properties: {
      projectName: 'mock-project',
      status: 'provisioning',
      lastModified: '2026-03-25T09:00:00Z',
      sourceType: 'file',
      artifactCount: 5,
    },
    artifacts: [
      { id: 'mock-project/knowledgeHubs/onboarding-guide/knowledgeArtifacts/day-1-checklist.pdf', name: 'day-1-checklist.pdf', properties: { contentType: 'application/pdf', uploadStatus: 'Completed', createdAt: '2026-03-25T08:00:00Z' } },
      { id: 'mock-project/knowledgeHubs/onboarding-guide/knowledgeArtifacts/it-setup-guide.docx', name: 'it-setup-guide.docx', properties: { contentType: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', uploadStatus: 'Completed', createdAt: '2026-03-25T08:15:00Z' } },
      { id: 'mock-project/knowledgeHubs/onboarding-guide/knowledgeArtifacts/team-directory.xlsx', name: 'team-directory.xlsx', properties: { contentType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', uploadStatus: 'InProgress', createdAt: '2026-03-25T08:30:00Z' } },
      { id: 'mock-project/knowledgeHubs/onboarding-guide/knowledgeArtifacts/org-chart.pdf', name: 'org-chart.pdf', properties: { contentType: 'application/pdf', uploadStatus: 'InProgress', createdAt: '2026-03-25T08:45:00Z' } },
      { id: 'mock-project/knowledgeHubs/onboarding-guide/knowledgeArtifacts/welcome-letter.txt', name: 'welcome-letter.txt', properties: { contentType: 'text/plain', uploadStatus: 'InProgress', createdAt: '2026-03-25T09:00:00Z' } },
    ],
  },
  {
    id: 'mock-project/knowledgeHubs/product-docs-kb',
    name: 'product-docs-kb',
    type: 'foundryiq',
    properties: { description: 'Product docs from AI Foundry knowledge base', projectName: 'mock-project', status: 'ready', lastModified: '2026-03-20T16:45:00Z', sourceType: 'foundryiq', foundryIQConfig: { endpoint: 'https://foundry.example.com', knowledgeBaseName: 'product-docs' } },
  },
  {
    id: 'mock-project/knowledgeHubs/engineering-wiki',
    name: 'engineering-wiki',
    type: 'foundryiq',
    properties: { projectName: 'mock-project', status: 'ready', lastModified: '2026-03-18T11:20:00Z', sourceType: 'foundryiq', foundryIQConfig: { endpoint: 'https://foundry.example.com', knowledgeBaseName: 'eng-wiki' } },
  },
  {
    id: 'mock-project/knowledgeHubs/support-tickets',
    name: 'support-tickets',
    type: 'ai-search',
    properties: { description: 'Azure AI Search index over support tickets', projectName: 'mock-project', status: 'ready', lastModified: '2026-03-23T15:30:00Z', sourceType: 'ai-search', aiSearchConfig: { endpoint: 'https://search.example.com', indexName: 'support-tickets' } },
  },
  {
    id: 'mock-project/knowledgeHubs/product-catalog',
    name: 'product-catalog',
    type: 'ai-search',
    properties: { projectName: 'mock-project', status: 'error', lastModified: '2026-03-21T08:00:00Z', sourceType: 'ai-search', aiSearchConfig: { endpoint: 'https://search.example.com', indexName: 'product-catalog' } },
  },
  {
    id: 'mock-project/knowledgeHubs/compliance-docs',
    name: 'compliance-docs',
    type: 'ai-search',
    properties: { description: 'Regulatory compliance documentation', projectName: 'mock-project', status: 'ready', lastModified: '2026-03-19T13:15:00Z', sourceType: 'ai-search', aiSearchConfig: { endpoint: 'https://search.example.com', indexName: 'compliance' } },
  },
];

// =============================================================================
// DATAPLANE REQUEST HELPER
// =============================================================================

async function dataplaneRequest<T>(
  accessToken: string,
  method: 'GET' | 'POST' | 'PUT' | 'DELETE',
  url: string,
  body?: unknown,
  timeoutMs: number = REQUEST_TIMEOUT
): Promise<T> {
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);

  try {
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (!isMockMode()) {
      headers['Authorization'] = `Bearer ${accessToken}`;
    }

    const options: RequestInit = {
      method,
      headers,
      signal: controller.signal,
    };

    if (body !== undefined) {
      options.body = JSON.stringify(body);
    }

    const response = await fetch(url, options);

    if (!response.ok) {
      const errorText = await response.text();
      let errorMessage: string;
      let errorCode: string | undefined;

      if (response.status >= 500) {
        errorMessage = 'An internal error occurred. Please try your request again.';
      } else {
        try {
          const errorJson = JSON.parse(errorText);
          errorCode = errorJson.error?.code;
          errorMessage = errorJson.error?.message ?? errorJson.message ?? errorText;
        } catch {
          errorMessage = errorText || response.statusText;
        }
      }

      throw new KnowledgeHubApiError(errorMessage, response.status, errorCode);
    }

    // DELETE may return 200 or 204 with no/empty body
    if (method === 'DELETE' || response.status === 204) {
      return undefined as T;
    }

    return response.json();
  } catch (error) {
    if (error instanceof Error && error.name === 'AbortError') {
      throw new Error('Request timed out. Please try again.');
    }
    throw error;
  } finally {
    clearTimeout(timeoutId);
  }
}

// =============================================================================
// URL BUILDERS
// =============================================================================

function getKnowledgeHubsBaseUrl(projectName: string): string {
  return `${getLogicEndpoint()}/automationProjects/${encodeURIComponent(projectName)}/knowledgeHubs`;
}

function getKnowledgeHubUrl(projectName: string, hubName: string): string {
  return `${getKnowledgeHubsBaseUrl(projectName)}/${encodeURIComponent(hubName)}`;
}

// =============================================================================
// PUBLIC API
// =============================================================================

/**
 * List all knowledge hubs for an automation project.
 */
export async function listKnowledgeHubs(
  accessToken: string,
  projectName: string
): Promise<KnowledgeHub[]> {
  if (isMockMode()) {
    return [...mockHubs];
  }

  const url = `${getKnowledgeHubsBaseUrl(projectName)}?api-version=${API_VERSION}`;
  const response = await dataplaneRequest<{ content?: KnowledgeHub[]; value?: KnowledgeHub[] }>(accessToken, 'GET', url);
  const hubs = response.content ?? response.value ?? [];
  // Normalize status values from the API to internal portal status codes
  // The API may return 'provisioningState' instead of 'status'
  return hubs.map(h => {
    const props = h.properties as unknown as Record<string, unknown>;
    const rawStatus = props.status ?? props.provisioningState ?? 'pending';
    return {
      ...h,
      properties: { ...h.properties, status: normalizeKnowledgeHubStatus(rawStatus) },
    };
  });
}

/**
 * List artifacts for a knowledge hub.
 * @deprecated Use hub.artifacts directly from listKnowledgeHubs() instead.
 * This function is kept for backward compatibility but artifacts are now returned inline.
 */
export async function listArtifacts(
  accessToken: string,
  projectName: string,
  hubName: string
): Promise<KnowledgeArtifact[]> {
  if (isMockMode()) {
    const hub = mockHubs.find((h) => h.name === hubName);
    return [...(hub?.artifacts ?? [])];
  }

  const url = `${getKnowledgeHubUrl(projectName, hubName)}/knowledgeArtifacts?api-version=${API_VERSION}`;
  const response = await dataplaneRequest<{ content?: unknown[]; value?: unknown[] }>(accessToken, 'GET', url);
  return normalizeArtifacts(response.content ?? response.value ?? [], projectName, hubName);
}

/**
 * Normalize artifact list response to the KnowledgeArtifact ARM shape.
 * Handles both the current shape ({ id, name, properties }) and older
 * API responses that used a flat shape ({ name, contentType, status, uploadedAt }).
 */
function normalizeArtifacts(
  rawArtifacts: unknown[],
  projectName: string,
  hubName: string
): KnowledgeArtifact[] {
  return rawArtifacts.map((raw): KnowledgeArtifact => {
    const r = raw as Record<string, unknown>;
    if (r.properties && typeof r.properties === 'object') {
      return raw as KnowledgeArtifact;
    }
    // Legacy flat shape: { name, contentType, status, uploadedAt }
    const legacyStatus = r.status as string | undefined;
    const uploadStatus: KnowledgeArtifactStatus =
      legacyStatus === 'indexed' ? 'Completed' :
      legacyStatus === 'error' ? 'Failed' : 'InProgress';
    const name = r.name as string;
    const contentType = r.contentType as string | undefined;
    return {
      id: `${projectName}/knowledgeHubs/${hubName}/knowledgeArtifacts/${name}`,
      name,
      properties: {
        ...(contentType !== undefined && { contentType }),
        uploadStatus,
        createdAt: (r.uploadedAt as string | undefined) ?? new Date().toISOString(),
      },
    };
  });
}

/**
 * Get a single knowledge hub by name.
 */
export async function getKnowledgeHub(
  accessToken: string,
  projectName: string,
  hubName: string
): Promise<KnowledgeHub> {
  if (isMockMode()) {
    const hub = mockHubs.find((h) => h.name === hubName);
    if (!hub) throw new Error(`Knowledge hub '${hubName}' not found`);
    return { ...hub };
  }

  const url = `${getKnowledgeHubUrl(projectName, hubName)}?api-version=${API_VERSION}`;
  return dataplaneRequest<KnowledgeHub>(accessToken, 'GET', url);
}

/**
 * Create a file-based knowledge hub.
 */
export async function createFileHub(
  accessToken: string,
  projectName: string,
  request: CreateFileHubRequest
): Promise<KnowledgeHub> {
  if (isMockMode()) {
    const newHub: KnowledgeHub = {
      id: `${projectName}/knowledgeHubs/${request.hubName}`,
      name: request.hubName,
      type: 'file',
      properties: {
        ...(request.description ? { description: request.description } : {}),
        projectName,
        status: 'pending',
        lastModified: new Date().toISOString(),
        sourceType: 'file',
        artifactCount: 0,
      },
    };
    mockHubs.push(newHub);
    return newHub;
  }

  const url = `${getKnowledgeHubUrl(projectName, request.hubName)}?api-version=${API_VERSION}`;
  return dataplaneRequest<KnowledgeHub>(accessToken, 'PUT', url, {
    properties: {
      sourceType: 'file' as KnowledgeHubType,
      description: request.description,
      projectName,
    },
  });
}

/**
 * Create a FoundryIQ-connected knowledge hub.
 */
export async function createFoundryIQHub(
  accessToken: string,
  projectName: string,
  request: CreateFoundryIQHubRequest
): Promise<KnowledgeHub> {
  if (isMockMode()) {
    const newHub: KnowledgeHub = {
      id: `${projectName}/knowledgeHubs/${request.hubName}`,
      name: request.hubName,
      type: 'foundryiq',
      properties: {
        ...(request.description ? { description: request.description } : {}),
        projectName,
        status: 'pending',
        lastModified: new Date().toISOString(),
        sourceType: 'foundryiq',
        foundryIQConfig: request.config,
      },
    };
    mockHubs.push(newHub);
    return newHub;
  }

  const url = `${getKnowledgeHubUrl(projectName, request.hubName)}?api-version=${API_VERSION}`;
  return dataplaneRequest<KnowledgeHub>(accessToken, 'PUT', url, {
    properties: {
      sourceType: 'foundryiq' as KnowledgeHubType,
      description: request.description,
      projectName,
      foundryIQConfig: request.config,
    },
  });
}

/**
 * Create an AI Search-connected knowledge hub.
 */
export async function createAISearchHub(
  accessToken: string,
  projectName: string,
  request: CreateAISearchHubRequest
): Promise<KnowledgeHub> {
  if (isMockMode()) {
    const newHub: KnowledgeHub = {
      id: `${projectName}/knowledgeHubs/${request.hubName}`,
      name: request.hubName,
      type: 'ai-search',
      properties: {
        ...(request.description ? { description: request.description } : {}),
        projectName,
        status: 'pending',
        lastModified: new Date().toISOString(),
        sourceType: 'ai-search',
        aiSearchConfig: request.config,
      },
    };
    mockHubs.push(newHub);
    return newHub;
  }

  const url = `${getKnowledgeHubUrl(projectName, request.hubName)}?api-version=${API_VERSION}`;
  return dataplaneRequest<KnowledgeHub>(accessToken, 'PUT', url, {
    properties: {
      sourceType: 'ai-search' as KnowledgeHubType,
      description: request.description,
      projectName,
      aiSearchConfig: request.config,
    },
  });
}

/**
 * Read a File as a base64-encoded string.
 */
function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      // result is "data:<mime>;base64,<data>" — strip the prefix
      const dataUrl = reader.result as string;
      const base64 = dataUrl.substring(dataUrl.indexOf(',') + 1);
      resolve(base64);
    };
    reader.onerror = () => reject(new Error(`Failed to read file: ${file.name}`));
    reader.readAsDataURL(file);
  });
}

/**
 * Upload file artifacts to an existing knowledge hub.
 * Sends each file as a base64-encoded PUT to the knowledgeArtifacts endpoint per the design spec:
 *   PUT .../knowledgeHubs/{hubName}/knowledgeArtifacts/{artifactName}?api-version=...
 */
export async function uploadArtifacts(
  accessToken: string,
  projectName: string,
  hubName: string,
  files: File[],
  fileDescriptions?: Record<string, string>
): Promise<void> {
  if (isMockMode()) {
    // In mock mode, simulate a short delay
    await new Promise((resolve) => setTimeout(resolve, 500));
    const hub = mockHubs.find((h) => h.name === hubName);
    if (hub) {
      hub.properties.artifactCount = (hub.properties.artifactCount ?? 0) + files.length;
      hub.properties.lastModified = new Date().toISOString();
      // Add artifacts to the hub's embedded artifacts list using the ARM shape
      if (!hub.artifacts) {
        hub.artifacts = [];
      }
      for (const file of files) {
        const desc = fileDescriptions?.[file.name];
        hub.artifacts.push({
          id: `${projectName}/knowledgeHubs/${hubName}/knowledgeArtifacts/${file.name}`,
          name: file.name,
          properties: {
            ...(desc ? { description: desc } : {}),
            contentType: file.type || 'application/octet-stream',
            uploadStatus: 'InProgress',
            createdAt: new Date().toISOString(),
          },
        });
      }
    }
    return;
  }

  for (const file of files) {
    const artifactName = file.name;
    const url = `${getKnowledgeHubUrl(projectName, hubName)}/knowledgeArtifacts/${encodeURIComponent(artifactName)}?api-version=${API_VERSION}`;
    const base64Content = await readFileAsBase64(file);

    await dataplaneRequest(accessToken, 'PUT', url, {
      properties: {
        description: fileDescriptions?.[file.name] ?? file.name,
        payload: {
          '$content-type': file.type || 'application/octet-stream',
          '$content': base64Content,
        },
      },
    }, UPLOAD_TIMEOUT);
  }
}

/**
 * Delete a knowledge hub.
 */
export async function deleteKnowledgeHub(
  accessToken: string,
  projectName: string,
  hubName: string
): Promise<void> {
  if (isMockMode()) {
    const idx = mockHubs.findIndex((h) => h.name === hubName);
    if (idx >= 0) mockHubs.splice(idx, 1);
    return;
  }

  const url = `${getKnowledgeHubUrl(projectName, hubName)}?api-version=${API_VERSION}`;
  await dataplaneRequest<void>(accessToken, 'DELETE', url);
}

/**
 * Delete a single artifact from a knowledge hub.
 */
export async function deleteArtifact(
  accessToken: string,
  projectName: string,
  hubName: string,
  artifactName: string
): Promise<void> {
  if (isMockMode()) {
    const hub = mockHubs.find((h) => h.name === hubName);
    if (hub?.artifacts) {
      hub.artifacts = hub.artifacts.filter((a) => a.name !== artifactName);
      hub.properties.artifactCount = hub.artifacts.length;
    }
    return;
  }

  const url = `${getKnowledgeHubUrl(projectName, hubName)}/knowledgeArtifacts/${encodeURIComponent(artifactName)}?api-version=${API_VERSION}`;
  await dataplaneRequest<void>(accessToken, 'DELETE', url);
}

/**
 * Delete multiple knowledge hubs.
 */
export async function deleteKnowledgeHubs(
  accessToken: string,
  projectName: string,
  hubNames: string[]
): Promise<void> {
  await Promise.all(
    hubNames.map((name) => deleteKnowledgeHub(accessToken, projectName, name))
  );
}
